import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, Truck, MapPin, Timer } from "lucide-react";
import { CONTACT, IMAGES } from "../../data";
import { openInquiry } from "../../lib/inquiry";

const headingLines = ["Expedited Freight", "Without the", "Heavy-Truck Complexity"];

const trust = [
  { icon: Truck, t: "Cargo-Van & Sprinter Specialists" },
  { icon: MapPin, t: "Canada–U.S. Cross-Border Coordination" },
  { icon: Timer, t: "Time-Critical Freight Solutions" },
];

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mapY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-white pt-[132px] md:pt-[150px] pb-20 md:pb-28" data-testid="hero">
      {/* Map as background element */}
      <motion.div
        style={{ y: reduce ? 0 : mapY }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end w-full lg:w-[62%]"
        aria-hidden="true"
      >
        <img src={IMAGES.map} alt="" className="h-[80%] md:h-[92%] w-auto object-contain opacity-[0.16] md:opacity-25 translate-x-[8%]" />
      </motion.div>

      <div className="container-oyf relative z-10">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 mb-6"
          >
            <span className="h-[2px] w-8 bg-oyf-yellow" />
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-oyf-medgrey">Canada–U.S. Expedited Logistics</span>
          </motion.div>

          <h1 className="font-display font-extrabold text-oyf-black tracking-tight leading-[0.98] text-[2.6rem] sm:text-5xl lg:text-[4.4rem]">
            {headingLines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  className="block"
                  initial={reduce ? { opacity: 0 } : { y: "110%" }}
                  animate={reduce ? { opacity: 1 } : { y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? (
                    <><span className="bg-oyf-yellow px-2 box-decoration-clone">Heavy-Truck</span> Complexity</>
                  ) : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-6 max-w-xl text-oyf-medgrey text-base md:text-lg leading-relaxed"
          >
            One York Freight coordinates time-sensitive freight across Canada and the United States through agile cargo-van and Sprinter-van transportation. From pickup to final delivery, we help businesses move urgent loads with speed, communication and dependable cross-border coordination.
          </motion.p>

          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
            {trust.map((it) => (
              <div key={it.t} className="flex items-center gap-2 text-sm font-semibold text-oyf-black">
                <it.icon size={16} className="text-oyf-yellow" strokeWidth={2.5} />
                {it.t}
              </div>
            ))}
          </div>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button onClick={() => openInquiry("Hero Button")} className="inline-flex items-center justify-center gap-2 bg-oyf-yellow text-oyf-black font-bold px-7 py-4 rounded-md hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#202124] transition-transform duration-200" data-testid="hero-quote-btn">
              Send Inquiry <ArrowRight size={18} />
            </button>
            <a href={CONTACT.tel} className="inline-flex items-center justify-center gap-2 border-2 border-oyf-charcoal text-oyf-charcoal font-bold px-7 py-4 rounded-md hover:bg-oyf-charcoal hover:text-white transition-colors duration-200" data-testid="hero-call-btn">
              <Phone size={17} /> Call {CONTACT.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
